import { useContext, useState } from 'react';

import { DeleteOutline } from '@mui/icons-material';
import { Box, ListItem, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

import Button from '@/components/Buttons/Button';
import { Dialog, DialogButton, DialogContentText } from '@/components/Dialog';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { NavigationContext } from '@/pages/DataHub/NavigationContext';
import useDataHubSelector from '@/pages/DataHub/useDataHubSelector';
import { setActiveDataHubId } from '@/slices/dataHubSlice';
import { getTimeSince } from '@/utils/getTimeSince';
import { DataHub } from 'types/dataHub';

import { RootState } from '../../../store';

const DataHubDropdownItem = ({ dataHub }: { dataHub: DataHub }) => {
  const { navigateToDataHub } = useContext(NavigationContext);
  const dispatch = useAppDispatch();
  const [confirmClearDialogOpen, setConfirmClearDialogOpen] = useState(false);
  const { deleteDataHub } = useDataHubSelector();
  const { activeDataHubId } = useSelector((state: RootState) => state.dataHub);
  const isActive = dataHub.id === activeDataHubId;

  return (
    <ListItem
      sx={{
        m: 0.5,
        pl: 1,
        pr: 0,
        cursor: 'pointer',
        backgroundColor: isActive ? '#e84e0f29' : '#f8f8f81A',
        width: 'auto',
        position: 'relative'
      }}
      onClick={() => dispatch(setActiveDataHubId(dataHub.id))}
    >
      {isActive && (
        <Box
          sx={{
            position: 'absolute',
            width: 5,
            height: 5,
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            right: 10
          }}
        />
      )}
      <Box
        alignItems='flex-start'
        display='flex'
        flexDirection='column'
      >
        <Typography
          noWrap
          sx={{ fontWeight: 500, maxWidth: 163 }}
          variant='caption'
        >
          {dataHub.name}
        </Typography>
        <Typography
          sx={{ fontSize: 10, color: 'info.main' }}
          variant='caption'
        >
          {`${dataHub.symbols.length} items, last saved ${getTimeSince(dataHub.savedAt)}`}
        </Typography>
        <Button
          size='xsmall'
          sx={{
            height: 18,
            mt: 1,
            py: 0.2,
            backgroundColor: '#f8f8f833',
            border: 'none',
            fontSize: 10,
            px: 1.5
          }}
          variant='outlined'
          onClick={e => {
            e.stopPropagation();
            setConfirmClearDialogOpen(true);
          }}
        >
          <DeleteOutline sx={{ fontSize: '12px!important', m: '0!important' }} />
          Delete
        </Button>
      </Box>
      <Dialog
        buttons={
          <>
            <DialogButton
              variant='contained'
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                deleteDataHub(dataHub.id);
                navigateToDataHub(0);
                setConfirmClearDialogOpen(false);
                setTimeout(() => dispatch(setActiveDataHubId('')), 100);
              }}
            >
              Delete Data Hub
            </DialogButton>
            <DialogButton
              variant='outlined'
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setConfirmClearDialogOpen(false);
              }}
            >
              Cancel
            </DialogButton>
          </>
        }
        open={confirmClearDialogOpen}
        title={`Delete '${dataHub.name}' Data Hub?`}
        onClose={() => {
          setConfirmClearDialogOpen(false);
        }}
      >
        <DialogContentText>You will permanently lose this saved Data Hub.</DialogContentText>
      </Dialog>
    </ListItem>
  );
};

export default DataHubDropdownItem;
